import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import { FLOORPLAN_UUID_QUERY_PARAM, SECTION_QUERY_PARAM } from 'floorplan/pages/native-floorplan/const';
import { useFloorplanMapDate } from './use-floorplan-map-date.hook';
import { useFloorplanMapQuery } from 'data/queries/useFloorplanMapQuery.hook';
import { useFloorplanViewState } from './use-floorplan-view-state.hook';
import { useSelectedSectionUuid } from './use-selected-section.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { replace } from 'store/router/actions';
import { searchParams as SearchParams } from 'shared/types/search-params';
import { FloorplanPlacementSection } from 'floorplan/pages/native-floorplan/types';
import { findActiveFloorplan } from 'floorplan/pages/native-floorplan/utils/active-floorplan';
import {
  collectSections,
  matchesSection,
  resolveSelectedSection,
} from 'floorplan/pages/native-floorplan/utils/sections';

interface UseFloorplanSectionFilterValues {
  sections: FloorplanPlacementSection[];
  /** Null means every section — the "All" chip. */
  selectedSectionUuid: string | null;
  /** How many filters are set. Sections are the only one for now; features are next. */
  activeFilterCount: number;
  /** Placements the map will keep lit, i.e. what the CTA offers to show. */
  matchingCount: number;
  isLoading: boolean;
  onSectionSelect: (sectionUuid: string | null) => void;
  onClearAll: VoidFunction;
}

/**
 * The floorplan filters sheet's state: the sections of the floor on screen, and which one is picked.
 *
 * The placements come from the same query the map draws from — one request, already cached — so the
 * chips can only ever offer sections the user can actually see, and the CTA can count what the map
 * would light up.
 */
export const useFloorplanSectionFilter = (): UseFloorplanSectionFilterValues => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const queryParams = useSearchParams<SearchParams>();
  const { floorplans } = useFloorplanViewState();
  const { date } = useFloorplanMapDate();
  const urlSectionUuid = useSelectedSectionUuid();

  // The sheet renders outside the map's subtree, so the floor is resolved from the url the same way
  // the map resolves it.
  const activeFloorplan = findActiveFloorplan(floorplans, queryParams[FLOORPLAN_UUID_QUERY_PARAM]);
  const { placements, isLoading } = useFloorplanMapQuery({ floorplanUuid: activeFloorplan?.uuid, date });

  const sections = useMemo(() => collectSections(placements), [placements]);
  // What the url asks for, as it applies to this floor — see `resolveSelectedSection`.
  const selectedSectionUuid = useMemo(
    () => resolveSelectedSection(sections, urlSectionUuid),
    [sections, urlSectionUuid],
  );
  const matchingCount = useMemo(
    () => placements.filter(placement => matchesSection(placement, selectedSectionUuid)).length,
    [placements, selectedSectionUuid],
  );

  const onSectionSelect = useCallback(
    (sectionUuid: string | null) => {
      // "All" drops the param instead of storing a value for it: no filter is the default, and the
      // url should say so by staying clean. `qs.stringify` leaves undefined values out.
      const nextParams = { ...queryParams, [SECTION_QUERY_PARAM]: sectionUuid ?? undefined };

      dispatch(replace(`${pathname}?${qs.stringify(nextParams)}`));
    },
    [dispatch, pathname, queryParams],
  );

  const onClearAll = useCallback(() => onSectionSelect(null), [onSectionSelect]);

  return useMemo(
    () => ({
      sections,
      selectedSectionUuid,
      activeFilterCount: selectedSectionUuid ? 1 : 0,
      matchingCount,
      isLoading,
      onSectionSelect,
      onClearAll,
    }),
    [sections, selectedSectionUuid, matchingCount, isLoading, onSectionSelect, onClearAll],
  );
};
